import React, { useEffect } from 'react';

import { useTrackCustomEvent } from '../useTrackCustomEvent';

import * as _hotkeys from './hotkeyDictionary';
import * as _monacoHotkeys from './monacoHotkeyDictionary';

// awkwardly reexport as namespace until our babel config supports
// https://github.com/tc39/proposal-export-ns-from
export const hotkeys = _hotkeys;
export const monacoHotkeys = _monacoHotkeys;

export interface ModifiersKeys {
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
}

export const explorerKeyboardShortcuts = {
  submitOperation: hotkeys.submitOperation,
  saveOperation: hotkeys.saveOperation,
  saveOperationAs: hotkeys.saveOperationAs,
  toggleSearch: hotkeys.toggleSearch,
  toggleShortcutModal: hotkeys.toggleShortcutModal,
  openCommandPalette: hotkeys.openCommandPalette,
  escape: hotkeys.escape,
  ...monacoHotkeys,
};

export const sdlPageKeyboardShortcuts = {
  toggleSearch: hotkeys.toggleSearch,
  toggleShortcutModal: hotkeys.toggleShortcutModal,
  peekPreview: hotkeys.peekPreview,
  toggleCommentVisibility: hotkeys.toggleCommentVisibility,
  toggleDeprecatedVisibility: hotkeys.toggleDeprecatedVisibility,
};

export const proposalEditorKeyboardShortcuts = {
  saveRevision: hotkeys.saveRevision,
  toggleShortcutModal: hotkeys.toggleShortcutModal,
  openCommandPalette: hotkeys.openCommandPalette,
  formatDocument: monacoHotkeys.formatDocument,
};

/**
 * Register a keypress listener to a given action. If the keypress includes the
 * meta key modifier, this will also register it as an editor action. This is
 * because Monaco stops propogation of any meta key presses when focussed.
 * Unfortunately they do not expose a way to turn that off similar to
 * `alwaysConsumeMouseWheel` so we have to register an editor action to capture
 * the event.
 */
export function useKeypressListener(
  { keyboardEventCode, modifierKeys }: _hotkeys.HotkeyDictionaryItem,
  action: (event: KeyboardEvent) => void,
) {
  // This is a ref so that we dont need to reattach a new listener every time
  // the action changes
  const actionRef = React.useRef(action);
  actionRef.current = action;

  const trackCustomEvent = useTrackCustomEvent();

  const { altKey, ctrlKey, metaKey, shiftKey } = modifierKeys;
  // When a new keypress is received, register it to context and add a window
  // event listener
  useEffect(() => {
    const keypressHandler = (event: KeyboardEvent) => {
      // Matching key pressed
      if (event.code === keyboardEventCode) {
        // All specified modifier keys are pressed
        if (
          !!altKey === event.altKey &&
          !!ctrlKey === event.ctrlKey &&
          !!metaKey === event.metaKey &&
          !!shiftKey === event.shiftKey
        ) {
          actionRef.current(event);
          trackCustomEvent({
            action: 'used_keyboard_shortcut',
            category: 'Keyboard Shortcuts',
            label: `keyboard event code: ${event.code}`,
          });

          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    window.addEventListener('keydown', keypressHandler, {
      // capture events in the capturing phase instead of the default bubbling phase
      // monaco swallows Meta key presses, those events will not bubble up
      capture: true,
    });

    return () => {
      window.removeEventListener('keydown', keypressHandler, { capture: true });
    };
  }, [keyboardEventCode, altKey, ctrlKey, metaKey, shiftKey, trackCustomEvent]);
}
